import { SiteModelLevel } from './site-model.types';

/**
 * Caméra du mode « Suivre le tracker » (ADR-017, démo) : le glisser tourne autour du personnage au
 * lieu de déplacer la carte, et l'étage affiché suit le sien. Fonctions pures, sans MapLibre.
 */

/** Rotation par pixel glissé à l'horizontale, inclinaison par pixel à la verticale (degrés). */
const BEARING_DEG_PER_PX = 0.4;
const PITCH_DEG_PER_PX = 0.3;
/** Au-delà de cette hauteur au-dessus de son plancher, le personnage est dans l'escalier. */
const ON_FLOOR_M = 0.4;

export interface CameraAngles {
  bearing: number;
  pitch: number;
}

/** Caméra après un glisser de (`dx`, `dy`) pixels : gauche / droite tourne, haut / bas incline. */
export function orbit(
  cam: CameraAngles,
  dx: number,
  dy: number,
  limits: { minPitch: number; maxPitch: number }
): CameraAngles {
  const b = cam.bearing - dx * BEARING_DEG_PER_PX;
  return {
    bearing: ((((b + 180) % 360) + 360) % 360) - 180,
    pitch: Math.min(limits.maxPitch, Math.max(limits.minPitch, cam.pitch - dy * PITCH_DEG_PER_PX)),
  };
}

/**
 * Étage à afficher pour un personnage dont les pieds sont à `feetY` : le sien, ou celui du haut dès
 * qu'il monte l'escalier (la coupe de l'étage du bas lui couperait la tête) ; `null` sans étage.
 */
export function floorToShow(levels: SiteModelLevel[], feetY: number): SiteModelLevel | null {
  const sorted = [...levels].sort((a, b) => a.z - b.z);
  return sorted.find(l => l.z >= feetY - ON_FLOOR_M) ?? sorted.at(-1) ?? null;
}

/**
 * Bascule d'étage au changement seulement : un étage choisi à la main reste affiché jusqu'au
 * prochain changement d'étage du personnage.
 */
export class AutoFloor {
  #last: string | null = null;

  /** Étage à afficher s'il vient de changer, sinon `null`. */
  next(levelId: string | null): string | null {
    if (levelId === null || levelId === this.#last) {
      return null;
    }
    this.#last = levelId;
    return levelId;
  }

  /** Suivi relancé : l'étage du personnage sera affiché tout de suite. */
  reset(): void {
    this.#last = null;
  }
}
